'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';

import { ArrowDownSmall, Box2, WalletRegular } from '@zoomit/icons';
import { Sheet } from '@zoomit/mobile-sheet';
import { cn, Link } from '@zoomit/primitives';
import type { ProductStoreItem } from '@zoomit/web-kit';
import { AnimatedImage, ProductPrice, Score, useHistoryState } from '@zoomit/web-kit';
import { Anchor, Button, cssVar, Hr, theme, Typography } from '@zoomit/zoomit-ds';

import { useStoreLink } from '../../hooks/useStoreLink';
import { useOrigin } from '../../providers/OriginProvider';
import AdvertisingReportage from './AdvertisingReportage';
import InstallmentProviderItem from './InstallmentProviderItem';

type Props = {
  positionId: number;
  isMorePurchaseItemsVisible?: boolean;
} & ProductStoreItem;

const ProductStoreListItemMobile = (props: Props) => {
  const {
    isAdvertisement,
    storeLogo,
    storeTitle,
    purchaseItems,
    url,
    storeId,
    storeLinkId,
    isMorePurchaseItemsVisible = true,
    score,
    reviewCounts,
    positionId,
  } = props;
  const [linkAttr, setLinkAttr] = useState({
    href: url,
    rel: 'nofollow',
    target: '_blank',
  });

  const { baseMediaUrl } = useOrigin();

  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const contentVariants = {
    open: { height: 'auto', overflow: 'initial' },
    collapsed: { height: 0, overflow: 'hidden' },
  };

  const toggleDetails = () => {
    setIsDetailsOpen((state) => !state);
  };

  const price = purchaseItems[0]?.price ?? 0;
  const mainDescription = purchaseItems[0]?.description;
  const { generateLink } = useStoreLink();

  useEffect(() => {
    const attr = {
      href: generateLink({
        store: {
          storeId,
          storeLinkId,
          url,
        },
        price,
        position: `${positionId + 1}`,
      }),
      rel: 'nofollow',
      target: '_blank',
    };

    setLinkAttr(attr);
  }, []);

  const installmentProvidersModalStates = useHistoryState<boolean>({
    id: `installment-providers-modal-${storeLinkId}`,
    initialState: false,
  });

  const installmentProvidersCount = props.installmentProviders?.length ?? 0;

  return (
    <div className="shadow-main bg-card h-full w-[inherit]">
      <div className="h-full px-2 pt-2">
        <div className="relative flex h-full flex-col justify-between px-2 pt-2 pb-3">
          {isAdvertisement && <AdvertisingReportage position="top-right" />}
          <div>
            <div className="relative flex items-center">
              <Link {...linkAttr} className="relative inline-block h-12 w-12 place-content-center">
                <AnimatedImage
                  width={48}
                  height={48}
                  alt="shop-image-alt"
                  src={`${baseMediaUrl}/${storeLogo?.id}`}
                  placeholder="blur"
                  blurDataURL={storeLogo?.preview}
                  style={
                    price === 0
                      ? { mixBlendMode: 'luminosity', objectFit: 'contain' }
                      : { objectFit: 'contain' }
                  }
                  sizes="48px"
                />
              </Link>
              <Link {...linkAttr} className="mr-3">
                <Typography tag="span" lineHeight={2} fontSize={1.4} fontWeight="bold">
                  {storeTitle}
                </Typography>
                {score && <Score score={score} reviewCounts={reviewCounts} />}
              </Link>
            </div>
            <Link {...linkAttr}>
              <Typography
                spacing={{ mt: 'x' }}
                tag="p"
                fontSize={1.2}
                lineHeight={2}
                textColor={cssVar(theme.grey1)}
                className="fa line-clamp-2"
              >
                {mainDescription}
              </Typography>
            </Link>
            <div className="flex gap-2">
              {props.productCondition === 'Used' && (
                <div className="bg-grey6 text-subhead text-text border-borderPrimary my-2 flex w-fit items-center gap-1 rounded-full border px-2 leading-[2]">
                  <Box2 size="sm" className="fill-grey1" />
                  کالای کارکرده
                  {/* <ArrowDownSmall size="xs" /> */}
                </div>
              )}

              {props.installmentProviders && (
                <button
                  className="bg-grey6 text-subhead text-text border-borderPrimary my-2 flex items-center gap-1 rounded-full border px-2 leading-[2]"
                  onClick={installmentProvidersModalStates.open}
                >
                  <WalletRegular size="sm" fill="#F90" />
                  امکان خرید قسطی
                  <ArrowDownSmall size="xs" />
                </button>
              )}
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between">
            <div className="flex flex-col">
              <Link {...linkAttr}>
                <div className="flex items-center">
                  <ProductPrice price={price} />
                </div>
              </Link>
            </div>
            {price === 0 ? (
              <Anchor variant="outline" {...linkAttr}>
                <Typography
                  tag="span"
                  fontSize={1.2}
                  lineHeight={2}
                  textColor={cssVar(theme.grey1)}
                  fontWeight="bold"
                >
                  مشاهده فروشگاه
                </Typography>
              </Anchor>
            ) : (
              <Anchor size="fit" style={{ width: 80 }} variant="contained" {...linkAttr}>
                خرید
              </Anchor>
            )}
          </div>
        </div>
      </div>
      {purchaseItems.length > 1 && isMorePurchaseItemsVisible && (
        <div className="border-grey6 flex border-t px-4 py-2">
          <Button
            size="auto"
            variant="text"
            rightIcon={
              <motion.div
                animate={{
                  rotate: isDetailsOpen ? 180 : 0,
                }}
              >
                <ArrowDownSmall size="sm" />
              </motion.div>
            }
            spacing={{ px: 'x0' }}
            onClick={toggleDetails}
          >
            <Typography tag="span" fontSize={1.2} lineHeight={2} textColor={cssVar(theme.grey1)}>
              مشاهده مدل‌ها
            </Typography>
          </Button>
        </div>
      )}
      <AnimatePresence initial={false}>
        {isDetailsOpen && (
          <motion.section
            key="content"
            initial="collapsed"
            animate="open"
            exit="collapsed"
            variants={contentVariants}
            transition={{ duration: 0.4, ease: [0.04, 0.62, 0.23, 0.98] }}
          >
            <div className="px-4 pb-3">
              {purchaseItems.map((pk, index) => (
                <div
                  key={`pk-${index}`}
                  className={cn(
                    'flex flex-nowrap justify-between',
                    index !== purchaseItems.length - 1 && 'mb-4',
                  )}
                >
                  <div className="w-7/12">
                    <Typography tag="span" fontSize={1.2} lineHeight={2} className="fa">
                      {pk.description}
                    </Typography>
                  </div>
                  <div className="w-5/12 text-left">
                    <ProductPrice price={pk.price} />
                  </div>
                </div>
              ))}
            </div>
          </motion.section>
        )}
      </AnimatePresence>
      <Sheet isOpen={installmentProvidersModalStates.isOpen} onClose={installmentProvidersModalStates.close}>
        <Sheet.Container>
          <Sheet.Content>
            <div className="flex flex-col px-11 pt-11 pb-7">
              {props.installmentProviders?.map(({ id, title, logo, description, url }, index) => (
                <>
                  <InstallmentProviderItem
                    key={id}
                    provider={{
                      url,
                      title,
                      description,
                      logo: {
                        src: `${baseMediaUrl}/${logo.id}`,
                        blurDataUrl: logo.preview,
                        alt: logo.alt,
                      },
                    }}
                  />
                  {index !== installmentProvidersCount - 1 && <Hr />}
                </>
              ))}
            </div>
            <div className="bg-background1 flex w-full items-center justify-center px-11 py-4">
              <Button variant="outline" size="full width" onClick={installmentProvidersModalStates.close}>
                بستن
              </Button>
            </div>
          </Sheet.Content>
        </Sheet.Container>
        <Sheet.Backdrop />
      </Sheet>
    </div>
  );
};

export default ProductStoreListItemMobile;
